import { generatePath } from '@remix-run/react';
import { toast } from 'react-toastify';
import type { PlanLevel } from '~/lib/.client/plans';
import { requestWithAuth } from '~/lib/.client/request-with-auth';
import { CLIENT_ORIGIN, WINDOW_RELOAD_TIMEOUT } from '~/lib/constants';
import type { OrganizationData } from '~/types/organization';
import type { StripeSubscriptionSession, SubscriptionInterval } from '~/types/subscription';

const makeRequestWithAuth = requestWithAuth(CLIENT_ORIGIN);

// #region Personal subscription

type GetStripePersonalSubscriptionInfoResponse =
  | {
      data: StripeSubscriptionSession;
    }
  | {
      error: string;
    };

export async function getStripePersonalSubscriptionInfo(
  level: PlanLevel,
  interval: SubscriptionInterval,
  promotionCode?: string,
): Promise<GetStripePersonalSubscriptionInfoResponse> {
  const url = new URL('/api/stripe_proxy/subscriptions/session', CLIENT_ORIGIN);

  url.searchParams.set('level', level);
  url.searchParams.set('interval', interval);

  if (promotionCode) {
    url.searchParams.set('promotionCode', String(promotionCode));
  }

  try {
    const data = await makeRequestWithAuth<StripeSubscriptionSession>(url.toString(), undefined, 'POST');

    if (data) {
      return { data };
    }

    return { error: 'Failed to get subscription info' };
  } catch (error: any) {
    return { error: error.message };
  }
}

export async function cancelPersonalSubscriptionDowngrade(level: PlanLevel) {
  const url = new URL('/api/stripe_proxy/subscriptions', CLIENT_ORIGIN);

  url.searchParams.set('level', level);

  return makeRequestWithAuth(url.toString(), undefined, 'PUT')
    .then(() => {
      toast.success('Downgrade successfully canceled');

      setTimeout(() => {
        window.location.reload();
      }, WINDOW_RELOAD_TIMEOUT);
    })
    .catch(() => {
      toast.error('Error while canceling downgrade');
    });
}

export async function updatePersonalSubscription(
  level: PlanLevel,
  interval: SubscriptionInterval,
  promotionCode?: string,
) {
  const url = new URL('/api/stripe_proxy/subscriptions', CLIENT_ORIGIN);

  url.searchParams.set('level', level);
  url.searchParams.set('interval', interval);
  url.searchParams.set('actionRequiredReturnUrl', window.location.href);

  if (promotionCode) {
    url.searchParams.set('promotionCode', String(promotionCode));
  }

  return makeRequestWithAuth(url.toString(), undefined, 'PUT', {
    402: async (response) => {
      const responseBody = (await response.json()) as { redirectToUrl: string };
      const error = new Error('Payment requires additional authentication') as any;
      error.status = 402;
      error.redirectToUrl = responseBody.redirectToUrl;
      throw error;
    },
  });
}

export async function cancelPersonalSubscription() {
  return makeRequestWithAuth(`api/membership/cancel_at_period_end`, undefined, 'POST')
    .then(() => {
      toast.success('Subscription successfully canceled');

      setTimeout(() => {
        window.location.reload();
      }, WINDOW_RELOAD_TIMEOUT);
    })
    .catch(() => {
      toast.error('Error while canceling subscription');
    });
}

export async function renewPersonalSubscription() {
  return makeRequestWithAuth(`api/membership/reactivate`, undefined, 'POST')
    .then(() => {
      toast.success('Subscription successfully renewed');

      setTimeout(() => {
        window.location.reload();
      }, WINDOW_RELOAD_TIMEOUT);
    })
    .catch(() => {
      toast.error('Error while renewing subscription');
    });
}

export async function cancelPersonalSubscriptionUpdate() {
  return makeRequestWithAuth(`api/membership/pending_update`, undefined, 'DELETE')
    .then(() => {
      toast.success('Subscription update successfully canceled');

      setTimeout(() => {
        window.location.reload();
      }, WINDOW_RELOAD_TIMEOUT);
    })
    .catch(() => {
      toast.error('Error while canceling subscription update');
    });
}

// #endregion

// #region Team subscription

type GetStripeOrgSubscriptionInfoResponse =
  | {
      data: StripeSubscriptionSession;
    }
  | {
      error: string;
    };

export async function getStripeOrgSubscriptionInfo(
  organization: OrganizationData,
  level: PlanLevel,
  interval: SubscriptionInterval,
  promotionCode?: string,
): Promise<GetStripeOrgSubscriptionInfoResponse> {
  const path = generatePath('/api/organizations/:provider/:slug/stripe_proxy/subscriptions/session', organization);

  const url = new URL(path, CLIENT_ORIGIN);

  url.searchParams.set('level', level);
  url.searchParams.set('interval', interval);

  if (promotionCode) {
    url.searchParams.set('promotionCode', String(promotionCode));
  }

  try {
    const data = await makeRequestWithAuth<StripeSubscriptionSession>(url.toString(), undefined, 'POST');

    if (data) {
      return { data };
    }

    return { error: 'Failed to get subscription info' };
  } catch (error: any) {
    return { error: error.message };
  }
}

export async function updateTeamSubscription(
  organization: OrganizationData,
  level: PlanLevel,
  interval: SubscriptionInterval,
  promotionCode?: string,
) {
  const url = new URL(
    `api/organizations/${organization.provider}/${organization.slug}/stripe_proxy/subscriptions`,
    CLIENT_ORIGIN,
  );

  url.searchParams.set('level', level);
  url.searchParams.set('interval', interval);
  url.searchParams.set('actionRequiredReturnUrl', window.location.href);

  if (promotionCode) {
    url.searchParams.set('promotionCode', String(promotionCode));
  }

  return makeRequestWithAuth(url.toString(), undefined, 'PUT', {
    402: async (response) => {
      const responseBody = (await response.json()) as { redirectToUrl: string };

      const error = new Error('Payment requires additional authentication') as any;
      error.status = 402;
      error.redirectToUrl = responseBody.redirectToUrl;
      throw error;
    },
  });
}

export async function cancelTeamSubscriptionDowngrade(organization: OrganizationData, level: PlanLevel) {
  const url = new URL(
    `api/organizations/${organization.provider}/${organization.slug}/stripe_proxy/subscriptions?level=${level}`,
    CLIENT_ORIGIN,
  );

  return makeRequestWithAuth(url.toString(), undefined, 'PUT')
    .then(() => {
      toast.success('Downgrade successfully canceled');

      setTimeout(() => {
        window.location.reload();
      }, WINDOW_RELOAD_TIMEOUT);
    })
    .catch(() => {
      toast.error('Error while canceling downgrade');
    });
}

export async function cancelTeamSubscription(organization: OrganizationData) {
  return makeRequestWithAuth(
    `api/organizations/${organization.provider}/${organization.slug}/subscription/cancel_at_period_end`,
    undefined,
    'POST',
  )
    .then(() => {
      toast.success('Subscription successfully canceled');

      setTimeout(() => {
        window.location.reload();
      }, WINDOW_RELOAD_TIMEOUT);
    })
    .catch(() => {
      toast.error('Error while canceling subscription');
    });
}

export async function renewTeamSubscription(organization: OrganizationData) {
  return makeRequestWithAuth(
    `api/organizations/${organization.provider}/${organization.slug}/subscription/reactivate`,
    undefined,
    'POST',
  )
    .then(() => {
      toast.success('Subscription successfully renewed');

      setTimeout(() => {
        window.location.reload();
      }, WINDOW_RELOAD_TIMEOUT);
    })
    .catch(() => {
      toast.error('Error while renewing subscription');
    });
}

export async function cancelTeamSubscriptionUpdate(organization: OrganizationData) {
  return makeRequestWithAuth(
    `/api/organizations/${organization.provider}/${organization.slug}/subscription/pending_update`,
    undefined,
    'DELETE',
  )
    .then(() => {
      toast.success('Subscription update successfully canceled');

      setTimeout(() => {
        window.location.reload();
      }, WINDOW_RELOAD_TIMEOUT);
    })
    .catch(() => {
      toast.error('Error while canceling subscription update');
    });
}

// #endregion
